"use client"

import { asArray } from "@/lib/as-array"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { X } from "lucide-react"
import { CompanyContactSelector, CompanyContactSelectorProps } from "./company-contact-selector"
import { GetJobsJobIdHiringManagers200Item } from "openapi/api/model"
import { useDeleteContactsHiringManagers, useGetJobsJobId, useGetJobsJobIdHiringManagers } from "openapi/api/endpoints"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"



/**
 * The dialog that adds and removes a job's hiring managers.
 *
 * This file used to also export an inline list with a round teal `+` beside
 * it, and nothing in the app rendered any of it — so the add-and-remove flow
 * existed, worked, and was unreachable. The job console draws the list now, and
 * this is the dialog its "Manage" opens.
 */
export function JobHiringManagersDialog({
    jobId,
    open,
    onOpenChange,
}: {
    jobId: string
    open: boolean
    onOpenChange: (open: boolean) => void
}) {
    const { data: job } = useGetJobsJobId(jobId, { query: { enabled: !!jobId } })
    const { data, refetch: refetchManagers } = useGetJobsJobIdHiringManagers(jobId, {
        query: { enabled: !!jobId },
    })
    /*
      `asArray`, not the payload. An error body or an envelope is not a list,
      and `hiringManagers?.map` on one took the whole job page down — this
      dialog is mounted by the page, so its crash is the page's crash.
    */
    const hiringManagers = asArray<GetJobsJobIdHiringManagers200Item>(data)
    const queryClient = useQueryClient()

    const { mutateAsync: deleteHiringManager, isPending } = useDeleteContactsHiringManagers()

    /** The console reads the same list, so it is refreshed with this one. */
    const refresh = async () => {
        await refetchManagers()
        await queryClient.invalidateQueries({
            queryKey: [`/jobs/${jobId}/hiring-managers`],
        })
    }

    const handleRemoveHiringManager = async (id: string) => {
        if (!jobId) return

        try {
            await deleteHiringManager({ data: { contact_id: id, job_id: jobId } })
            await refresh()
            toast.success("Hiring manager removed successfully")
        } catch {
            toast.error("Failed to remove hiring manager")
        }
    }

    return (
        <DialogShell
                open={open}
                onOpenChange={onOpenChange}
                wide
                title="Manage Hiring Managers"
                description="Add or remove the people hiring for this position."
            >
                {hiringManagers.length > 0 && (
                    <div className="mb-5">
                        <p className="text-[9.5px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                            Current
                        </p>
                        {/* Hairline rows in the dialog, not a stack of `bg-muted`
                            boxes, and `@`-sized so the rail this also opens in
                            is what decides the sizes. */}
                        <div className="mt-[9px] divide-y border-t">
                            {hiringManagers.map((manager, index) => {
                                const name = `${manager.contacts?.first_name ?? ""} ${
                                    manager.contacts?.last_name ?? ""
                                }`.trim()
                                return (
                                    <div key={index} className="flex items-center gap-2.5 py-2.5">
                                        <Avatar className="size-8 flex-none border">
                                            <AvatarFallback className="text-[11px]">
                                                {name
                                                    .split(" ")
                                                    .map((part) => part[0])
                                                    .join("")
                                                    .slice(0, 2)
                                                    .toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-[12.5px] font-medium leading-[1.3]">
                                                {name || "—"}
                                            </p>
                                            <p className="truncate text-[11.5px] leading-[1.4] text-muted-foreground">
                                                {[manager.contacts?.title, manager.contacts?.email]
                                                    .filter(Boolean)
                                                    .join(" · ")}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            disabled={isPending}
                                            onClick={() => handleRemoveHiringManager(manager.contact_id ?? "")}
                                            aria-label={`Remove ${name || "hiring manager"}`}
                                            className="flex size-8 flex-none items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-destructive disabled:opacity-40"
                                        >
                                            <X className="size-4" />
                                        </button>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}

                <CompanyContactSelector
                    jobId={jobId}
                    companyId={(job as unknown as {company_id:string})?.company_id ?? ''}
                    onAddContact={() => void refresh()}
                    selectedContacts={hiringManagers.map((m) => ({
                        id: m.contact_id ?? '',
                        name: m.contacts?.first_name + ' ' + m.contacts?.last_name,
                        email: m.contacts?.email ?? '',
                        position: m.contacts?.title ?? '',
                    })) as CompanyContactSelectorProps["selectedContacts"]}
                />
        </DialogShell>
    )
}
